import {
  and,
  ControlProps,
  getControlPath,
  RankedTester,
  rankWith,
  scopeEndsWith,
} from "@jsonforms/core";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { FormHelperText, MenuItem, Select, TextField } from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import { CountryCode } from "@home-health-notify/domain";
import { formatPhoneNumberInput } from "hhn-commons";
import { styled } from "@mui/material/styles";

const PhoneNumberContainer = styled("div")({
  display: "flex",
  gap: "8px",
  width: "100%",
  alignItems: "flex-start",
});

const CountryCodeContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const CountryCodeSelect = styled(Select)({
  width: "140px",
});

const PhoneNumberField = styled(TextField)({
  flex: 1,
});

const PhoneNumberWithCountryCodeRenderer = ({
  path,
  data,
  handleChange,
  uischema,
  schema,
  required,
}: ControlProps) => {
  const { label } = uischema;
  const { properties } = schema;
  const countryCodeOptions = properties?.countryCode?.oneOf || [];
  const phoneData = data || { countryCode: CountryCode.US, phoneNumber: "" };

  const [phoneNumberError, setPhoneNumberError] = useState<
    string | undefined
  >();

  const [countryCodeError, setCountryCodeError] = useState<
    string | undefined
  >();

  const { core } = useJsonForms();

  useEffect(() => {
    if (core?.validationMode === "ValidateAndShow") {
      const phoneNumberError = core.errors?.find(
        e => `${path}.phoneNumber` === getControlPath(e),
      );
      setPhoneNumberError(phoneNumberError?.message);

      const countryCodeError = core.errors?.find(
        e => `${path}.countryCode` === getControlPath(e),
      );
      setCountryCodeError(countryCodeError?.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [core?.errors]);

  const handleCountryCodeChange = (event: any) => {
    handleChange(path, {
      ...phoneData,
      countryCode: event.target.value,
    });
  };

  const handlePhoneNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    // Remove all non-digit characters
    const cleanedNumber = input.replace(/\D/g, "");
    handleChange(path, {
      ...phoneData,
      phoneNumber: cleanedNumber ? parseInt(cleanedNumber, 10) : undefined,
    });
  };

  const maxLength = phoneData.countryCode === CountryCode.US ? 14 : 10;

  return (
    <PhoneNumberContainer>
      <CountryCodeContainer>
        <CountryCodeSelect
          value={phoneData.countryCode || CountryCode.US}
          onChange={handleCountryCodeChange}
          variant="outlined"
          error={!!countryCodeError}
        >
          {countryCodeOptions.map((option: any) => (
            <MenuItem key={option.const} value={option.const}>
              {option.title}
            </MenuItem>
          ))}
        </CountryCodeSelect>
        <FormHelperText error={!!countryCodeError}>
          {countryCodeError}
        </FormHelperText>
      </CountryCodeContainer>
      <PhoneNumberField
        required={required}
        label={label?.toString() || "Phone Number"}
        value={formatPhoneNumberInput(
          String(phoneData.phoneNumber) || "",
          phoneData.countryCode,
        )}
        type="tel"
        onChange={handlePhoneNumberChange}
        error={!!phoneNumberError}
        helperText={phoneNumberError || ""}
        slotProps={{ htmlInput: { maxLength } }}
      />
    </PhoneNumberContainer>
  );
};

export const phoneNumberWithCountryCodeTester: RankedTester = rankWith(
  10,
  and(scopeEndsWith("phoneNumberWithCountryCode")),
);

export default withJsonFormsControlProps(PhoneNumberWithCountryCodeRenderer);
